import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
 PieChart,
 Pie,
 Cell,
 Tooltip,
 Legend,
 ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Loader2, Calendar, Users, CheckCircle, XCircle, Clock, ArrowLeft } from "lucide-react";
import api from "../../api/client";

const FILTERS = [
 { label: "7 Days", value: "7", icon: <Calendar className="w-4 h-4" /> },
 { label: "1 Month", value: "30", icon: <Calendar className="w-4 h-4" /> },
 { label: "3 Months", value: "90", icon: <Calendar className="w-4 h-4" /> },
 { label: "1 Year", value: "365", icon: <Calendar className="w-4 h-4" /> },
 { label: "All Time", value: "all", icon: <Calendar className="w-4 h-4" /> },
];

const COLORS = [
 "oklch(0.85 0.17 91.36)", // yellow - pending
 "oklch(0.8 0.15 156.73)", // green - accepted
 "oklch(0.8 0.13 29.23)" // red - rejected
];

export default function RecruiterApplicationsAnalytics() {
 const navigate = useNavigate();
 const [apps, setApps] = useState([]);
 const [filtered, setFiltered] = useState([]);
 const [loading, setLoading] = useState(true);
 const [filter, setFilter] = useState("all");
 const [error, setError] = useState("");
 const [hoveredRow, setHoveredRow] = useState(null);

 useEffect(() => {
 fetchApplications();
 }, []);

 const fetchApplications = async () => {
 try {
 setLoading(true);
 setError("");
 const res = await api.get("/recruiter/applications");
 
 let applicationsData = [];
 
 if (Array.isArray(res.data)) {
 applicationsData = res.data;
 } else if (res.data && Array.isArray(res.data.data)) {
 applicationsData = res.data.data;
 } else if (res.data && Array.isArray(res.data.applications)) {
 applicationsData = res.data.applications;
 } else {
 console.warn("Unexpected API response structure:", res.data);
 setError("Could not parse applications data. Please try again.");
 setLoading(false);
 return;
 }
 
 const validApps = applicationsData.map(app => {
 const applierName = app.fullName || 
 (app.applierEmail ? app.applierEmail.split('@')[0] : "Unknown Applicant");
 
 const applierEmail = app.applierEmail || "No email";
 const jobTitle = app.jobTitle || app.title || "Unknown Job";
 const jobId = app.jobId || "unknown";
 
 return {
 id: app.id,
 status: (app.status || "PENDING").toUpperCase(),
 appliedAt: app.appliedAt || new Date().toISOString(),
 applierName,
 applierEmail,
 jobId,
 jobTitle,
 raw: app
 };
 }).filter(app => app.id);
 
 setApps(validApps);
 setFiltered(validApps);
 
 } catch (err) {
 console.error("Failed to load applications data:", err);
 setError(
 err.response?.data?.message || 
 err.message || 
 "Failed to load applications data. Please try again later."
 );
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 if (filter === "all") {
 setFiltered(apps);
 } else {
 const days = parseInt(filter);
 const now = new Date();
 const cutoff = new Date(now.setDate(now.getDate() - days));
 cutoff.setHours(0, 0, 0, 0);

 setFiltered(
 apps.filter((a) => {
 try {
 const appliedDate = new Date(a.appliedAt);
 appliedDate.setHours(0, 0, 0, 0);
 return appliedDate >= cutoff;
 } catch (e) {
 console.error("Error parsing date:", a.appliedAt, e);
 return false;
 }
 })
 );
 }
 }, [filter, apps]);

 const pending = filtered.filter((a) => a.status === "PENDING").length;
 const accepted = filtered.filter((a) => a.status === "ACCEPTED").length;
 const rejected = filtered.filter((a) => a.status === "REJECTED").length;

 const pieData = [
 { name: "Pending", value: pending },
 { name: "Accepted", value: accepted },
 { name: "Rejected", value: rejected },
 ];

 const StatusBadge = ({ status }) => {
 const getStatusStyle = (status) => {
 switch (status.toUpperCase()) {
 case "PENDING":
 return {
 bg: "bg-yellow-100 ",
 text: "text-yellow-800 dark:text-yellow-300",
 border: "border-yellow-200 dark:border-yellow-800",
 icon: <Clock className="w-3 h-3 mr-1" />
 };
 case "ACCEPTED":
 return {
 bg: "bg-green-100 ",
 text: "text-green-800 dark:text-green-300",
 border: "border-green-200 dark:border-green-800",
 icon: <CheckCircle className="w-3 h-3 mr-1" />
 };
 case "REJECTED":
 return {
 bg: "bg-red-100 ",
 text: "text-red-800 dark:text-red-300",
 border: "border-red-200 dark:border-red-800",
 icon: <XCircle className="w-3 h-3 mr-1" />
 };
 default:
 return {
 bg: "bg-muted/50 ",
 text: "text-foreground/80 dark:text-gray-300",
 border: "border-gray-200 dark:border-gray-800",
 icon: null
 };
 }
 };

 const style = getStatusStyle(status);

 return (
 <span
 className={`px-3 py-1.5 rounded-full text-xs font-medium border flex items-center justify-center gap-1 transition-all duration-200 hover:scale-105 ${style.bg} ${style.text} ${style.border}`}
 >
 {style.icon}
 {status}
 </span>
 );
 };

 // Loading
 if (loading) {
 return (
 <div className="flex justify-center items-center min-h-screen ">
 
 <div className=" flex flex-col items-center gap-4">
 <div className="relative">
 <Loader2 className="w-12 h-12 animate-spin text-primary" />
 <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent rounded-full blur-xl"></div>
 </div>
 <p className="text-muted-foreground animate-pulse">Loading applications analytics...</p>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen from-background to-muted/20 py-8 relative">
 
 
 {/* Floating particles effect */}
 <div className="absolute inset-0 overflow-hidden pointer-events-none">
 {[...Array(20)].map((_, i) => (
 <div
 key={i}
 className="absolute w-1 h-1 bg-primary/20 rounded-full animate-float"
 style={{
 left: `${Math.random() * 100}%`,
 top: `${Math.random() * 100}%`,
 animationDelay: `${Math.random() * 5}s`,
 animationDuration: `${10 + Math.random() * 20}s`
 }}
 />
 ))}
 </div>

 <style>{`
 @keyframes float {
 0%, 100% { transform: translateY(0) rotate(0deg); }
 50% { transform: translateY(-20px) rotate(180deg); }
 }
 .animate-float {
 animation: float linear infinite;
 }
 `}</style>

 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
 <button
 onClick={() => navigate("/recruiter/dashboard")}
 className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-6 group"
 >
 <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
 Back to Dashboard
 </button>

 {/* Header */}
 <div className="mb-8">
 <div className="flex items-center gap-3 mb-2">
 <div className="p-2 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5">
 <Users className="w-6 h-6 text-primary" />
 </div>
 <h1 className="text-3xl font-bold text-foreground bg-clip-text bg-gradient-to-r from-primary to-primary/80">
 Applications Analytics
 </h1>
 </div>
 <p className="text-muted-foreground ml-12">
 Track and analyze application statistics for your job postings
 </p>
 </div>

 {error && (
 <motion.div
 initial={{ opacity: 0, y: -20 }}
 animate={{ opacity: 1, y: 0 }}
 className="mb-6"
 >
 <Alert variant="destructive" className="border-red-500/50 bg-red-50 ">
 <AlertDescription className="flex items-center justify-between">
 <span>{error}</span>
 <Button
 variant="outline"
 size="sm"
 onClick={fetchApplications}
 className="ml-4 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30"
 >
 Retry
 </Button>
 </AlertDescription>
 </Alert>
 </motion.div>
 )}

 {/* Filter Buttons with enhanced selection */}
 <Card className="mb-6 bg-gradient-to-br from-card to-card/50 border-border/50 shadow-lg backdrop-blur-sm">
 <CardContent className="pt-6">
 <div className="flex flex-wrap gap-3">
 {FILTERS.map((f) => (
 <Button
 key={f.value}
 variant={filter === f.value ? "default" : "outline"}
 onClick={() => setFilter(f.value)}
 className={`transition-all duration-300 transform hover:scale-105 active:scale-95 ${
 filter === f.value 
 ? "bg-gradient-to-r from-primary to-primary/80 shadow-lg shadow-primary/25 hover:shadow-primary/40" 
 : "hover:bg-accent hover:border-primary/30"
 }`}
 >
 <span className="flex items-center gap-2">
 {f.icon}
 {f.label}
 </span>
 </Button>
 ))}
 </div>
 
 {/* Active filter indicator */}
 <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
 <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
 <span>
 Showing: {filter === "all" ? "All Time" : `Last ${filter} days`} • {filtered.length} applications
 </span>
 </div>
 </CardContent>
 </Card>

 {/* Stats Overview Cards with hover effects */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
 <motion.div whileHover={{ y: -5, transition: { duration: 0.2 } }}>
 <Card className="bg-gradient-to-br from-yellow-50/50 to-yellow-100/20 dark:from-yellow-900/10 dark:to-yellow-900/5 border-yellow-200/50 dark:border-yellow-800/30 shadow-lg hover:shadow-xl hover:shadow-yellow-500/10 transition-all duration-300 group">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between mb-4">
 <div className="p-3 rounded-xl bg-gradient-to-r from-yellow-500/10 to-yellow-400/5 group-hover:from-yellow-500/20 group-hover:to-yellow-400/10 transition-all duration-300">
 <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
 </div>
 <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300 bg-yellow-100 px-3 py-1 rounded-full">
 Needs Review
 </span>
 </div>
 <div className="text-4xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">
 {pending}
 </div>
 <p className="text-sm text-muted-foreground">Pending Applications</p>
 <div className="mt-4 h-1 bg-gradient-to-r from-yellow-500 to-yellow-300 rounded-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
 </CardContent>
 </Card>
 </motion.div>

 <motion.div whileHover={{ y: -5, transition: { duration: 0.2 } }}>
 <Card className="bg-gradient-to-br from-green-50/50 to-green-100/20 dark:from-green-900/10 dark:to-green-900/5 border-green-200/50 dark:border-green-800/30 shadow-lg hover:shadow-xl hover:shadow-green-500/10 transition-all duration-300 group">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between mb-4">
 <div className="p-3 rounded-xl bg-gradient-to-r from-green-500/10 to-green-400/5 group-hover:from-green-500/20 group-hover:to-green-400/10 transition-all duration-300">
 <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
 </div>
 <span className="text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 px-3 py-1 rounded-full">
 Successful
 </span>
 </div>
 <div className="text-4xl font-bold text-green-600 dark:text-green-400 mb-2">
 {accepted}
 </div>
 <p className="text-sm text-muted-foreground">Accepted Applications</p>
 <div className="mt-4 h-1 bg-gradient-to-r from-green-500 to-green-300 rounded-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
 </CardContent>
 </Card>
 </motion.div>

 <motion.div whileHover={{ y: -5, transition: { duration: 0.2 } }}>
 <Card className="bg-gradient-to-br from-red-50/50 to-red-100/20 dark:from-red-900/10 dark:to-red-900/5 border-red-200/50 dark:border-red-800/30 shadow-lg hover:shadow-xl hover:shadow-red-500/10 transition-all duration-300 group">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between mb-4">
 <div className="p-3 rounded-xl bg-gradient-to-r from-red-500/10 to-red-400/5 group-hover:from-red-500/20 group-hover:to-red-400/10 transition-all duration-300">
 <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
 </div>
 <span className="text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 px-3 py-1 rounded-full">
 Not Selected
 </span>
 </div>
 <div className="text-4xl font-bold text-red-600 dark:text-red-400 mb-2">
 {rejected}
 </div>
 <p className="text-sm text-muted-foreground">Rejected Applications</p>
 <div className="mt-4 h-1 bg-gradient-to-r from-red-500 to-red-300 rounded-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
 </CardContent>
 </Card>
 </motion.div>
 </div>

 {/* Chart Section */}
 <motion.div 
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 >
 <Card className="mb-8 border-border/50 bg-gradient-to-br from-card to-card/50 shadow-lg backdrop-blur-sm overflow-hidden">
 <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/50 to-primary"></div>
 <CardHeader>
 <CardTitle className="text-lg font-semibold text-foreground">
 <span className="bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
 Applications Distribution
 </span>
 </CardTitle>
 <p className="text-sm text-muted-foreground">
 Visual breakdown of application statuses
 </p>
 </CardHeader>
 <CardContent>
 <div className="h-80">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie
 data={pieData}
 cx="50%"
 cy="50%"
 labelLine={false}
 label={({ name, percent }) =>
 `${name}: ${(percent * 100).toFixed(0)}%`
 }
 outerRadius={100}
 innerRadius={50}
 paddingAngle={4}
 dataKey="value"
 >
 {pieData.map((entry, index) => (
 <Cell 
 key={`cell-${index}`} 
 fill={COLORS[index]} 
 stroke="var(--border)"
 strokeWidth={2}
 className="hover:opacity-80 transition-opacity duration-200"
 />
 ))}
 </Pie>
 <Tooltip 
 contentStyle={{ 
 backgroundColor: 'var(--card)',
 border: '1px solid var(--border)',
 borderRadius: '8px',
 color: 'var(--foreground)',
 boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
 }}
 formatter={(value, name) => [value, name]}
 />
 <Legend 
 verticalAlign="bottom" 
 height={36}
 formatter={(value) => (
 <span className="text-sm text-foreground/80">{value}</span>
 )}
 />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </CardContent>
 </Card>
 </motion.div>

 {/* Table Section */}
 {/* <motion.div 
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.3 }}
 >
 <Card className="border-border/50 bg-gradient-to-br from-card to-card/50 shadow-lg backdrop-blur-sm overflow-hidden">
 <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/50 to-primary"></div>
 <CardHeader>
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <div>
 <CardTitle className="text-lg font-semibold text-foreground">
 <span className="bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
 Applications List
 </span>
 </CardTitle>
 <p className="text-sm text-muted-foreground">
 Showing <span className="font-semibold text-primary">{filtered.length}</span> of <span className="font-semibold">{apps.length}</span> total applications
 </p>
 </div>
 <Button
 variant="outline"
 onClick={fetchApplications}
 className="sm:w-auto border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-all duration-300 group"
 >
 <Loader2 className="w-4 h-4 mr-2 group-hover:animate-spin" />
 Refresh Data
 </Button>
 </div>
 </CardHeader>
 <CardContent>
 <div className="overflow-x-auto rounded-xl border border-border/50 shadow-inner">
 <table className="min-w-full divide-y divide-border/50">
 <thead className="bg-gradient-to-r from-muted/30 to-muted/10">
 <tr>
 <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
 Applicant
 </th>
 <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
 Email
 </th>
 <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
 Job Title
 </th>
 <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
 Applied Date
 </th>
 <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
 Status
 </th>
 </tr>
 </thead>
 <tbody className="divide-y divide-border/30">
 {filtered.length === 0 ? (
 <tr>
 <td
 colSpan="5"
 className="px-6 py-16 text-center"
 >
 <div className="flex flex-col items-center gap-4">
 <div className="p-4 rounded-full bg-gradient-to-r from-muted to-muted/50">
 <Users className="w-12 h-12 text-muted-foreground/50" />
 </div>
 <div>
 <p className="text-lg font-medium text-foreground">
 No applications found
 </p>
 <p className="text-sm text-muted-foreground mt-1">
 {filter === "all" 
 ? "You haven't received any applications yet."
 : "No applications found for the selected time period."
 }
 </p>
 </div>
 </div>
 </td>
 </tr>
 ) : (
 filtered.map((app, index) => (
 <tr
 key={app.id}
 className={`transition-all duration-200 ${
 hoveredRow === app.id 
 ? 'bg-gradient-to-r from-primary/5 to-primary/10' 
 : 'bg-card hover:'
 } ${index % 2 === 0 ? 'bg-opacity-50' : ''}`}
 onMouseEnter={() => setHoveredRow(app.id)}
 onMouseLeave={() => setHoveredRow(null)}
 >
 <td className="px-6 py-4">
 <div className="flex items-center group">
 <div className="relative">
 <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary/20 to-primary/10 flex items-center justify-center mr-3 transition-transform duration-200 group-hover:scale-110">
 <span className="text-sm font-semibold text-primary">
 {app.applierName?.charAt(0)?.toUpperCase() || "?"}
 </span>
 </div>
 <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-transparent rounded-full blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
 </div>
 <div>
 <div className="font-medium text-foreground group-hover:text-primary transition-colors duration-200">
 {app.applierName}
 </div>
 </div>
 </div>
 </td>
 <td className="px-6 py-4 text-sm">
 <a 
 href={`mailto:${app.applierEmail}`} 
 className="text-muted-foreground hover:text-primary hover:underline transition-all duration-200 inline-flex items-center gap-1 group/email"
 >
 {app.applierEmail}
 <span className="opacity-0 group-hover/email:opacity-100 transition-opacity duration-200">↗</span>
 </a>
 </td>
 <td className="px-6 py-4 text-sm">
 <button
 onClick={() => {
 if (app.jobId && app.jobId !== "unknown") {
 navigate(`/recruiter/jobs/${app.jobId}/applicants`);
 }
 }}
 className="font-medium text-primary hover:text-primary/80 transition-all duration-200 group/job relative"
 disabled={!app.jobId || app.jobId === "unknown"}
 >
 <span className="relative">
 {app.jobTitle}
 <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-primary/50 group-hover/job:w-full transition-all duration-300"></span>
 </span>
 </button>
 </td>
 <td className="px-6 py-4 text-sm text-muted-foreground">
 <div className="flex items-center gap-2">
 <Calendar className="w-4 h-4" />
 {new Date(app.appliedAt).toLocaleDateString('en-US', {
 year: 'numeric',
 month: 'short',
 day: 'numeric'
 })}
 </div>
 </td>
 <td className="px-6 py-4">
 <StatusBadge status={app.status} />
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>
 </CardContent>
 </Card>
 </motion.div> */}

 {/* Footer stats */}
 <motion.div 
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.4 }}
 className="mt-8 text-center text-sm text-muted-foreground"
 >
 <div className="inline-flex items-center gap-6 px-6 py-3 rounded-full bg-gradient-to-r from-muted/30 to-muted/10 border border-border/30">
 <div>
 <div className="font-semibold text-foreground">{apps.length}</div>
 <div className="text-xs">Total Applications</div>
 </div>
 <div className="h-6 w-px bg-border/50"></div>
 <div>
 <div className="font-semibold text-foreground">{filtered.length}</div>
 <div className="text-xs">Filtered</div>
 </div>
 <div className="h-6 w-px bg-border/50"></div>
 <div>
 <div className="font-semibold text-primary">{filter}</div>
 <div className="text-xs">Days Filter</div>
 </div>
 </div>
 </motion.div>
 </div>
 </div>
 );
}